// Warms all lazily code-split route chunks into the browser's module registry once
// the app is up.
//
// Why: route views are lazy (`component: () => import(...)`), so their JS chunks carry
// a content hash in the filename. After a deploy the server serves a new build and the
// old hashed chunks are gone. An already-open tab that then navigates to a not-yet-
// visited route can no longer fetch its chunk → blank screen until a manual reload.
//
// By eagerly invoking every route's import loader shortly after the first view has
// loaded, all route chunks end up in the JS module registry, which lives for the whole
// page lifetime and is never evicted. Subsequent navigation resolves from memory and
// never touches the network — so the running session keeps working across a deploy.
//
// We derive the set from the actual router definitions (constant + async routes) rather
// than a filesystem glob on purpose: that matches exactly the chunks vue-router would
// lazy-load, and avoids pulling non-route leaf components (some of which have optional
// dependencies) into the build graph.

import type { RouteRecordRaw } from "vue-router";
import constantRoutes from "@/router/modules/constantRoutes";
import asyncRoutes from "@/router/modules/asyncRoutes";

const CONCURRENCY = 4;

let started = false;

type ChunkLoader = () => unknown;

// Collect every route `component` that is a lazy import loader (a zero-arg function
// returning a Promise). The same Layout loader is reused across many routes; a Set
// dedupes it so we don't warm it repeatedly (dynamic imports are cached anyway).
function collectLoaders(
  routes: readonly RouteRecordRaw[],
  acc: Set<ChunkLoader>,
): void {
  for (const route of routes) {
    const component = (route as { component?: unknown }).component;
    if (typeof component === "function") {
      acc.add(component as ChunkLoader);
    }
    if (route.children?.length) {
      collectLoaders(route.children, acc);
    }
  }
}

// Minimal typing so we don't depend on lib.dom's requestIdleCallback being present.
type IdleScheduler = (cb: () => void, opts?: { timeout: number }) => number;

function schedule(cb: () => void): void {
  const ric = (
    window as Window & { requestIdleCallback?: IdleScheduler }
  ).requestIdleCallback;
  if (typeof ric === "function") {
    ric(() => cb(), { timeout: 2000 });
  } else {
    window.setTimeout(cb, 200);
  }
}

export function prefetchViews(): void {
  if (started) return;
  started = true;

  const loaders = new Set<ChunkLoader>();
  collectLoaders(constantRoutes as RouteRecordRaw[], loaders);
  collectLoaders(asyncRoutes, loaders);

  const queue = [...loaders];

  const runNext = (): void => {
    const load = queue.shift();
    if (!load) return;
    // A chunk that fails to warm is non-fatal: it simply falls back to an on-demand
    // load later. Prefetch must never surface an error to the user.
    Promise.resolve()
      .then(() => load())
      .catch(() => {})
      .finally(() => schedule(runNext));
  };

  for (let i = 0; i < CONCURRENCY; i++) {
    schedule(runNext);
  }
}
